import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import type { InterpolableTextProps, UniclientAction, UniclientWizardComponent } from '@lemonade-hq/uniclient-chat';
import { Button, Card, Flex, Layout, LinkButton, spacing, Text } from '@lemonade-hq/uniclient-theme';
import { InterpolableText } from '../../../elements/InterpolableText';
import { DynamicComponent } from '../../DynamicComponent';
import type { UniclientComponentAdapterProps } from '../../types';
import * as styles from './HeroCard.css';

type CoInsurance = {
  readonly icon?:
    | string
    | {
        readonly src: string;
        readonly alt: string;
        readonly delimiter?: '|' | 'x';
      };
  readonly text?: InterpolableTextProps;
};

interface HeroCardProps {
  readonly id?: string;
  readonly component?: UniclientWizardComponent<string> | UniclientWizardComponent<string>[];
  readonly action?: UniclientAction;
  readonly secondaryAction?: UniclientAction;
  readonly coInsurance?: CoInsurance;
  readonly disabled?: boolean;
  readonly disclaimer?: InterpolableTextProps;
  readonly loading?: boolean;
  readonly mainButtonExplainer?: InterpolableTextProps;
  readonly answers: WizardComponentAdapterProps['answers'];
  readonly onAction?: WizardComponentAdapterProps['onAction'];
  readonly adapterProps: Omit<UniclientComponentAdapterProps, 'answers' | 'componentProps'>;
}

const CoInsuranceIcon: React.FC<{ readonly coInsurance: CoInsurance }> = ({ coInsurance }) => {
  const { icon, text } = coInsurance;

  if (icon == null || text != null) return null;

  const getImageProps = () => {
    if (typeof icon === 'string') {
      return { src: icon };
    }

    return { alt: icon.alt, src: icon.src };
  };

  return (
    <Flex alignItems="center" gap={spacing.s08} justifyContent="center">
      <Layout {...getImageProps()} as="img" />
    </Flex>
  );
};

export const HeroCard: React.FC<HeroCardProps> = ({
  id,
  component,
  action,
  secondaryAction,
  coInsurance,
  disabled = false,
  loading,
  disclaimer,
  mainButtonExplainer,
  answers,
  onAction,
  adapterProps,
}) => (
  <>
    <Card borderRadius="lg" className={styles.card} gap={spacing.s16} id={id} p={spacing.s24} shadow="xl">
      <DynamicComponent
        {...adapterProps}
        answers={answers}
        component={component}
        disabled={disabled}
        onAction={onAction}
      />

      {secondaryAction?.content != null && (
        <LinkButton disabled={disabled || secondaryAction.disabled} onClick={() => void onAction?.(secondaryAction)}>
          <InterpolableText answers={answers} onAction={onAction} text={secondaryAction.content} />
        </LinkButton>
      )}

      {mainButtonExplainer != null && (
        <Text color="$textPrimary" mt={spacing.s16} textAlign="left" type="bodyMedium">
          <InterpolableText answers={answers} onAction={onAction} text={mainButtonExplainer} variant="inherit" />
        </Text>
      )}

      {action != null && (
        <Button
          disabled={disabled || action.disabled}
          loading={loading}
          onClick={() => void onAction?.(action)}
          variant="primary"
        >
          {action.content}
        </Button>
      )}

      {coInsurance?.text != null && (
        <Text className={styles.coInsuranceText} textAlign="center" type="bodySmall">
          <InterpolableText answers={answers} onAction={onAction} text={coInsurance.text} variant="inherit" />
        </Text>
      )}

      {coInsurance?.icon != null && <CoInsuranceIcon coInsurance={coInsurance} />}
    </Card>

    {disclaimer != null && (
      <Text color="$textTertiary" mt={spacing.s16} textAlign="center" type="bodySmall">
        <InterpolableText onAction={onAction} text={disclaimer} variant="inherit" />
      </Text>
    )}
  </>
);
